// src/Pages/Sessions/PrevSessions.jsx
import React, { useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { fetchPatientSessions } from "../../api";
import { UpdateSession } from "../../Components/Sessions/EditSession";
import "./EditSession.css";

export function PrevSessions({ patientId }) {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // ------------ helpers

  const sortStamp = (s) =>
    new Date(
      s.createdAt || s.sessionsAppointmentStart || s.updatedAt || 0,
    ).getTime();

  const byNewest = (a, b) =>
    sortStamp(b) - sortStamp(a) ||
    String(b._id || "").localeCompare(String(a._id || ""));

  const formatWhen = (iso) => {
    if (!iso) return "Unknown date";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "Unknown date";
    return d.toLocaleString([], {
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const titleFor = (s) =>
    `${s.sessionsServiceName || "—"} • ${formatWhen(
      s.sessionsAppointmentStart || s.createdAt,
    )}`;

  // ------------ effects

  useEffect(() => {
    async function fetchSessionsData() {
      try {
        const list = await fetchPatientSessions(patientId);
        const sorted = [...(list || [])].sort(byNewest);
        const makeSid = (s, i) =>
          String(
            s._id ??
              s.id ??
              `sess-${new Date(s.sessionsAppointmentStart || s.createdAt || s.updatedAt || 0).getTime()}-${i}`,
          );

        const augmented = sorted.map((s, i) => ({
          ...s,
          __sid: makeSid(s, i),
        }));

        setSessions(augmented);
      } catch (e) {
        console.error("Error loading sessions", e);
        setSessions([]);
        setSelectedSession(null);
        setSelectedSessionId(null);
      }
    }
    if (patientId) fetchSessionsData();
  }, [patientId]);

  useEffect(() => {
    if (!sessions?.length) {
      setSelectedSession(null);
      setSelectedSessionId(null);
      return;
    }
    if (
      !selectedSessionId ||
      !sessions.some((s) => s.__sid === selectedSessionId)
    ) {
      const first = sessions[0];
      setSelectedSession(first);
      setSelectedSessionId(first.__sid);
    }
  }, [sessions, selectedSessionId]);

  const handlePick = (s) => {
    setSelectedSession(s);
    setSelectedSessionId(s.__sid);
  };

  // Refresh after save, preserving selection
  const refreshSessionsKeepSelection = async () => {
    const list = await fetchPatientSessions(patientId);
    const sorted = [...(list || [])].sort(byNewest);
    const makeSid = (s, i) =>
      String(
        s._id ??
          s.id ??
          `sess-${new Date(s.sessionsAppointmentStart || s.createdAt || s.updatedAt || 0).getTime()}-${i}`,
      );

    const augmented = sorted.map((s, i) => ({
      ...s,
      __sid: makeSid(s, i),
    }));
    setSessions(augmented);
    if (selectedSession?._id) {
      const again = augmented.find((x) => x._id === selectedSession._id);
      if (again) {
        setSelectedSession(again);
        setSelectedSessionId(again.__sid);
      } else if (augmented[0]) {
        setSelectedSession(augmented[0]);
        setSelectedSessionId(augmented[0].__sid);
      } else {
        setSelectedSession(null);
        setSelectedSessionId(null);
      }
    }
  };

  // ------------ render

  return (
    <div>
      <style>{`
        .prev-sessions-dropdown .dropdown-toggle::after {
          display: none;
        }
        .prev-sessions-dropdown .dropdown-toggle {
          color: #0f3490;
        }
        .prev-sessions-dropdown .dropdown-toggle:hover,
        .prev-sessions-dropdown .dropdown-toggle:focus {
          background-color: #21d1d4 !important;
          color: white !important;
          border-color: #21d1d4 !important;
        }
        .prev-sessions-dropdown .dropdown-item {
          color: #0f3490;
        }
        .prev-sessions-dropdown .dropdown-item:hover,
        .prev-sessions-dropdown .dropdown-item:focus {
          background-color: #cfe3ff;
          color: #0f3490 !important;
        }
        .prev-sessions-dropdown .dropdown-item.active,
        .prev-sessions-dropdown .dropdown-item:active {
          background-color: #cfe3ff !important;
          color: #0f3490 !important;
        }
      `}</style>
      <div className="d-flex align-items-center gap-1 px-0 py-1 mb-1">

        {sessions.length > 0 && (
          <Dropdown className="prev-sessions-dropdown">
            <Dropdown.Toggle
              variant="outline-secondary"
              size="sm"
              className="rounded-0 d-flex align-items-center justify-content-between gap-2"
              style={{ minWidth: isMobile ? "160px" : "320px" }}
            >
              <span className="text-truncate">
                {selectedSession ? titleFor(selectedSession) : "Select session"}
              </span>
              <i className="fa-solid fa-chevron-down" style={{ fontSize: "0.95rem", flexShrink: 0 }} />
            </Dropdown.Toggle>
            <Dropdown.Menu style={{ minWidth: "320px", maxHeight: "320px", overflowY: "auto" }}>
              {sessions.map((s) => (
                <Dropdown.Item
                  key={s.__sid}
                  active={selectedSessionId === s.__sid}
                  onClick={() => handlePick(s)}
                >
                  <div className="fw-semibold">{s.sessionsServiceName || "—"}</div>
                  <div className="text-muted small border-bottom border-1">
                    {formatWhen(s.sessionsAppointmentStart || s.createdAt)}
                  </div>
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        )}

        {(() => {
          const apptCount = sessions.filter(s => !s.sessionsAdHoc && s.sessionsAppointmentId && s.sessionsAppointmentId !== "adhoc").length;
          const adHocCount = sessions.filter(s => s.sessionsAdHoc || !s.sessionsAppointmentId || s.sessionsAppointmentId === "adhoc").length;
          return (
            <div className="d-flex align-items-center gap-2">
              {apptCount > 0 && (
                <span className="d-flex align-items-center gap-1 text-primary small">
                  <span className="badge bg-primary">{apptCount}</span>
                  Appointment(s)
                </span>
              )}
              {adHocCount > 0 && (
                <span className="d-flex align-items-center gap-1 text-primary small">
                  <span className="badge bg-secondary">{adHocCount}</span>
                  Ad Hoc Notes
                </span>
              )}
            </div>
          );
        })()}

        {sessions.length === 0 && (
          <span className="text-muted small">No sessions available</span>
        )}
      </div>

      {selectedSession && (
        <UpdateSession
          key={selectedSession.__sid}
          sessionId={selectedSession._id}
          patientId={patientId}
          appointmentId={selectedSession.sessionsAppointmentId}
          serviceName={selectedSession.sessionsServiceName}
          appointmentStart={selectedSession.sessionsAppointmentStart}
          appointmentStatus={selectedSession.sessionsAppointmentStatus}
          sections={selectedSession.sections || {}}
          notepadContentR={selectedSession.notepadContent || ""}
          notepadContentType={selectedSession.notepadContentType || ""}
          handwritingImageR={selectedSession.handwritingImage || null}
          templateId={selectedSession.sessionsTemplateId}
          practiceId={selectedSession.sessionsPracticeId}
          resourceId={selectedSession.sessionsResourceId}
          capturedValuesR={selectedSession.capturedValues || {}}
          updatedAtR={selectedSession.updatedAt || null}
          onSaved={refreshSessionsKeepSelection}
          inline={true}
        />
      )}
    </div>
  );
}
